// Building style table — one look per player type (culture). Data only; the
// prefab builders and `restyle` read it, nothing branches on a style id.
// Design: docs/02-systems/building-styles.md.

/** Player types that have their own building look. `neutral` = unowned / unknown (the shared prefabs). */
export const BUILDING_STYLE_IDS = ['neutral', 'imperial', 'nordic', 'eastern'] as const;
export type BuildingStyleId = (typeof BUILDING_STYLE_IDS)[number];

/** Styles that map to a sim culture (`culture` city prop). */
export const CULTURE_STYLE_IDS: readonly BuildingStyleId[] = ['imperial', 'nordic', 'eastern'];

/** Material roles every prefab builder uses; all `#RRGGBB`. */
export interface StylePalette {
  readonly wall: string;
  readonly wallAlt: string;
  readonly trim: string;
  readonly roof: string;
  readonly roofAlt: string;
  readonly base: string;
  readonly door: string;
  readonly glass: string;
  /** Metal / gold ornament: finials, eagles, lantern tips. */
  readonly accent: string;
}

export type RoofShape = 'hip-low' | 'gable-steep' | 'swept-tiers' | 'shared';
export type BannerShape = 'vexillum' | 'pennant-mast' | 'nobori' | 'shared';

/** Steps of the one restyle pipeline (`restyle` in buildings_restyle.ts). */
export type RestyleOp = 'recolor' | 'team-accents' | 'crown' | 'centre';

/**
 * Prefabs that are drawn in-game as city centres, and the settlement tier each
 * stands for. These get the `centre` op list instead of the default one.
 */
export const CENTRE_PREFAB_TIER: Readonly<Record<string, 1 | 2 | 3>> = {
  house: 1,
  tavern: 2,
  castle: 3,
};

export interface BuildingStyle {
  readonly id: BuildingStyleId;
  /** Display name (legend, city panel). */
  readonly name: string;
  /** One-line look for the legend; text, never colour alone. */
  readonly blurb: string;
  readonly palette: StylePalette;
  readonly roof: RoofShape;
  readonly banner: BannerShape;
  /** Pipeline steps per prefab class; same for every culture today, data so a style can differ. */
  readonly ops: { readonly default: readonly RestyleOp[]; readonly centre: readonly RestyleOp[] };
}

const STANDARD_OPS = {
  default: ['recolor', 'team-accents', 'crown'],
  centre: ['centre'],
} as const;

export const BUILDING_STYLES: Readonly<Record<BuildingStyleId, BuildingStyle>> = {
  neutral: {
    id: 'neutral',
    name: 'Neutral',
    blurb: 'Unowned or unknown: the plain shared buildings.',
    palette: {
      wall: '#deb887',
      wallAlt: '#909090',
      trim: '#654321',
      roof: '#8b0000',
      roofAlt: '#606060',
      base: '#808080',
      door: '#654321',
      glass: '#87ceeb',
      accent: '#ffd700',
    },
    roof: 'shared',
    banner: 'shared',
    ops: STANDARD_OPS,
  },
  imperial: {
    id: 'imperial',
    name: 'Imperial',
    blurb: 'White marble, terracotta roofs, columns and domes.',
    palette: {
      wall: '#e8e0cc',
      wallAlt: '#d6cdb4',
      trim: '#d4a437',
      roof: '#b5472a',
      roofAlt: '#cf6a45',
      base: '#9a958a',
      door: '#5a3a22',
      glass: '#8fb8d0',
      accent: '#d4a437',
    },
    roof: 'hip-low',
    banner: 'vexillum',
    ops: STANDARD_OPS,
  },
  nordic: {
    id: 'nordic',
    name: 'Nordic',
    blurb: 'Dark timber halls, steep gables, dragon finials.',
    palette: {
      wall: '#8a5a32',
      wallAlt: '#a06c3c',
      trim: '#8c3b1f',
      roof: '#8c6a48',
      roofAlt: '#75573c',
      base: '#7a7a76',
      door: '#8c3b1f',
      glass: '#e6c86e',
      accent: '#b0b4b8',
    },
    roof: 'gable-steep',
    banner: 'pennant-mast',
    ops: STANDARD_OPS,
  },
  eastern: {
    id: 'eastern',
    name: 'Eastern',
    blurb: 'Vermilion pillars, swept charcoal roofs, pagoda tiers.',
    palette: {
      wall: '#f0ece0',
      wallAlt: '#ddd6c2',
      trim: '#c0392b',
      roof: '#70807c',
      roofAlt: '#5a6a66',
      base: '#4a3a30',
      door: '#c0392b',
      glass: '#d8d2b8',
      accent: '#d4a437',
    },
    roof: 'swept-tiers',
    banner: 'nobori',
    ops: STANDARD_OPS,
  },
};

/**
 * Style for a snapshot `culture` value. Missing / unknown -> `neutral`, so old
 * snapshots, old saves and unowned towns render exactly as before.
 */
export function styleForCulture(culture: unknown): BuildingStyleId {
  return culture === 'imperial' || culture === 'nordic' || culture === 'eastern'
    ? culture
    : 'neutral';
}
